
/**
 * Centralized timeout manager to prevent race conditions
 * and ensure proper cleanup of timeouts
 */

type TimeoutRef = {
  id: number;
  name: string;
  createdAt: number;
  expiresAt: number;
  cleared: boolean;
};

class TimeoutManager {
  private timeouts: Map<string, TimeoutRef> = new Map();
  private idCounter: number = 0;

  /**
   * Create a managed timeout that can be tracked and cleared
   * @param name Unique identifier for the timeout
   * @param callback Function to execute when timeout expires
   * @param delay Time in milliseconds to delay execution
   * @returns The timeout reference object
   */
  setTimeout(name: string, callback: () => void, delay: number): TimeoutRef {
    // Clear any existing timeout with the same name
    this.clearTimeout(name);
    
    const now = Date.now();
    const timeoutRef: TimeoutRef = {
      id: ++this.idCounter,
      name,
      createdAt: now,
      expiresAt: now + delay,
      cleared: false
    };
    
    console.log(`⏱️ Setting timeout: ${name}`, {
      delay,
      expiresAt: new Date(timeoutRef.expiresAt).toISOString(),
      timestamp: new Date().toISOString()
    });
    
    // Create the actual timeout
    const timeoutId = window.setTimeout(() => {
      try {
        // Mark as cleared before executing to prevent race conditions
        // if the callback tries to clear or set new timeouts
        if (this.timeouts.has(name)) {
          const ref = this.timeouts.get(name);
          if (ref) {
            ref.cleared = true;
          }
        }
        
        console.log(`⏱️ Executing timeout: ${name}`, {
          timestamp: new Date().toISOString()
        });
        
        // Execute the callback
        callback();
      } catch (error) {
        console.error(`Error in timeout callback (${name}):`, error);
      } finally {
        // Clean up
        this.timeouts.delete(name);
      }
    }, delay);
    
    // Store with the native timeout ID
    timeoutRef['nativeId'] = timeoutId;
    this.timeouts.set(name, timeoutRef);
    
    return timeoutRef;
  }
  
  /**
   * Clear a managed timeout by name
   * @param name The name of the timeout to clear
   * @returns true if a timeout was cleared, false otherwise
   */
  clearTimeout(name: string): boolean {
    if (!this.timeouts.has(name)) return false;
    
    const timeoutRef = this.timeouts.get(name);
    if (!timeoutRef || timeoutRef.cleared) return false;
    
    console.log(`⏱️ Clearing timeout: ${name}`, {
      remainingTime: timeoutRef.expiresAt - Date.now(),
      timestamp: new Date().toISOString()
    });
    
    // Clear the native timeout
    window.clearTimeout(timeoutRef['nativeId']);
    
    // Mark as cleared and remove from map
    timeoutRef.cleared = true;
    this.timeouts.delete(name);
    
    return true;
  }
  
  /**
   * Clear all managed timeouts
   * @returns The number of timeouts cleared
   */
  clearAllTimeouts(): number {
    let count = 0;
    
    for (const [name, _] of this.timeouts) {
      if (this.clearTimeout(name)) {
        count++;
      }
    }
    
    console.log(`⏱️ Cleared ${count} timeouts`);
    return count;
  }
  
  /**
   * Check if a timeout exists with the given name
   * @param name The timeout name to check
   * @returns true if the timeout exists and is active, false otherwise
   */
  hasTimeout(name: string): boolean {
    return this.timeouts.has(name) && !this.timeouts.get(name)?.cleared;
  }
  
  /**
   * Get remaining time for a timeout in milliseconds
   * @param name The name of the timeout
   * @returns The remaining time in ms, or -1 if the timeout doesn't exist
   */
  getRemainingTime(name: string): number {
    if (!this.hasTimeout(name)) return -1;
    
    const timeout = this.timeouts.get(name);
    return timeout ? Math.max(0, timeout.expiresAt - Date.now()) : -1;
  }
}

// Create singleton instance
export const timeoutManager = new TimeoutManager();
